// Mapa del Admin (especificación §2 y §4, D1–D5): cuatro áreas en el menú lateral (C47) y las secciones de cada área en la
// barra del módulo (C48). La pantalla abierta se deduce solo de la dirección: así el área y la sección quedan marcadas
// también dentro de una ficha (una empresa abierta marca «Empresas»), y recargar o compartir la dirección da lo mismo.

export type AreaKey = "home" | "orgs" | "ai" | "hub" | "account";

export type Area = { key: AreaKey; icon: string; labelKey: string; href: string };

export type Section = { key: string; icon: string; labelKey: string; href: (orgId: number | null) => string };

/** Áreas del menú lateral, en este orden (D2). Error Hub solo con su permiso; Mi cuenta va en el menú del avatar. */
export const areas: Area[] = [
  { key: "home", icon: "house", labelKey: "ADM_AREA_HOME", href: "/" },
  { key: "orgs", icon: "building-2", labelKey: "ADM_AREA_ORGS", href: "/Organizations/Index" },
  { key: "ai", icon: "cpu", labelKey: "ADM_AREA_AI", href: "/Ai/Config/Index" },
  { key: "hub", icon: "activity", labelKey: "ADM_AREA_HUB", href: "/ErrorHub" },
];

export const accountArea: Area = {
  key: "account",
  icon: "user",
  labelKey: "ADM_AREA_ACCOUNT",
  href: "/Account/Profile",
};

const org = (path: string) => (id: number | null) => (id ? `${path}?id=${id}` : "/Organizations/Index");
const fixed = (path: string) => () => path;

/** Secciones de una empresa abierta (D4), con su primera pantalla. */
export const orgSections: Section[] = [
  { key: "summary", icon: "layout-dashboard", labelKey: "ADM_ORG_SUMMARY", href: org("/Organizations/Detail") },
  { key: "people", icon: "users", labelKey: "ADM_ORG_PEOPLE", href: org("/Organizations/Usuarios") },
  { key: "security", icon: "shield-check", labelKey: "ADM_ORG_SECURITY", href: org("/Organizations/Politicas") },
  { key: "employees", icon: "user-cog", labelKey: "ADM_ORG_EMPLOYEES", href: org("/Organizations/Empleados") },
  { key: "messaging", icon: "messages-square", labelKey: "ADM_ORG_MESSAGING", href: org("/Organizations/Bandejas") },
  { key: "usage", icon: "life-buoy", labelKey: "ADM_ORG_USAGE", href: org("/Organizations/Soporte") },
];

/** Secciones de IA (D5): «Asignación» pasa a «Rutas», «Uso y trazas» a «Uso y costos»; «Avanzado» desaparece. */
export const aiSections: Section[] = [
  { key: "summary", icon: "layout-dashboard", labelKey: "ADM_AI_SUMMARY", href: fixed("/Ai/Config/Index") },
  { key: "providers", icon: "plug", labelKey: "ADM_AI_PROVIDERS", href: fixed("/Ai/Providers/Index") },
  { key: "models", icon: "box", labelKey: "ADM_AI_MODELS", href: fixed("/Ai/Modelos/Index") },
  { key: "routes", icon: "route", labelKey: "ADM_AI_ROUTES", href: fixed("/Ai/Rutas?route=TEXT_CHAT") },
  { key: "usage", icon: "chart-column", labelKey: "ADM_AI_USAGE", href: fixed("/Ai/Uso/Index") },
];

export const hubSections: Section[] = [
  { key: "summary", icon: "layout-dashboard", labelKey: "ADM_HUB_SUMMARY", href: fixed("/ErrorHub") },
  { key: "issues", icon: "circle-alert", labelKey: "ADM_HUB_ISSUES", href: fixed("/ErrorHub/Issues") },
  { key: "explore", icon: "search", labelKey: "ADM_HUB_EXPLORE", href: fixed("/ErrorHub/Explore") },
  { key: "performance", icon: "gauge", labelKey: "ADM_HUB_PERFORMANCE", href: fixed("/ErrorHub/Performance") },
  { key: "releases", icon: "package", labelKey: "ADM_HUB_RELEASES", href: fixed("/ErrorHub/Releases") },
];

export const accountSections: Section[] = [
  { key: "profile", icon: "user", labelKey: "ADM_ACC_PROFILE", href: fixed("/Account/Profile") },
  {
    key: "preferences",
    icon: "sliders-horizontal",
    labelKey: "ADM_ACC_PREFERENCES",
    href: fixed("/Account/Preferences"),
  },
  { key: "security", icon: "shield-check", labelKey: "ADM_ACC_SECURITY", href: fixed("/Account/Security") },
];

/** Pantalla de empresa → sección (especificación §4, incluidas las rutas Blazor que se funden). */
const orgSectionOf: Record<string, string> = {
  detail: "summary",
  modulos: "summary",
  abrircomousuario: "summary",
  usuarios: "people",
  invitaciones: "people",
  propietario: "people",
  recuperacionpropietario: "people",
  usuario: "people",
  agregarusuario: "people",
  agregarusuariocompletado: "people",
  politicas: "security",
  sesiones: "security",
  recuperacionacceso: "security",
  empleados: "employees",
  empleado: "employees",
  capacidades: "employees",
  empleadoconocimiento: "employees",
  conocimiento: "employees",
  conocimientobase: "employees",
  conocimientodetalle: "employees",
  conocimientofuente: "employees",
  conocimientofuentenueva: "employees",
  conocimientoterminos: "employees",
  aprobaciones: "employees",
  aprobacion: "employees",
  bandejas: "messaging",
  canales: "messaging",
  reglas: "messaging",
  conversaciones: "messaging",
  conversacion: "messaging",
  simulator: "messaging",
  chat: "messaging",
  client: "messaging",
  soporte: "usage",
  eventostecnicos: "usage",
  uso: "usage",
  trazas: "usage",
  auditoria: "usage",
};

export type Place = {
  area: AreaKey | null;
  /** Sección activa del área, o null en Inicio, el directorio de empresas y las páginas de estado. */
  section: string | null;
  /** Empresa abierta (id de la dirección), o null fuera de una empresa. */
  orgId: number | null;
  sections: Section[];
};

const positiveId = (value: string | null | undefined) => {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

/** Dónde está la persona según la dirección (pathname y query). */
export function placeOf(pathname: string, search: string): Place {
  const path = pathname.replace(/\/+$/, "") || "/";
  const lower = path.toLowerCase();
  const params = new URLSearchParams(search);
  if (path === "/") return { area: "home", section: null, orgId: null, sections: [] };
  if (lower.startsWith("/organizations")) {
    const parts = lower.split("/").filter(Boolean);
    const page = parts[1] ?? "index";
    const orgId = positiveId(params.get("id")) ?? (page === "detail" ? positiveId(parts[2]) : null);
    if (page === "index" || page === "crear" || !orgId)
      return { area: "orgs", section: null, orgId: null, sections: [] };
    return { area: "orgs", section: orgSectionOf[page] ?? "summary", orgId, sections: orgSections };
  }
  if (lower.startsWith("/ai")) {
    const section =
      lower.startsWith("/ai/providers") ||
      lower.startsWith("/ai/conexiones") ||
      lower.startsWith("/ai/avanzado/personalizado")
        ? "providers"
        : lower.startsWith("/ai/modelos") || lower.startsWith("/ai/avanzado/manual")
          ? "models"
          : lower.startsWith("/ai/rutas") ||
              lower.startsWith("/ai/asignacion") ||
              lower.startsWith("/ai/routing") ||
              lower.startsWith("/ai/usomodelos")
            ? "routes"
            : lower.startsWith("/ai/uso")
              ? "usage"
              : "summary";
    return { area: "ai", section, orgId: null, sections: aiSections };
  }
  if (lower.startsWith("/errorhub")) {
    const page = lower.split("/")[2] ?? "";
    const section = { issues: "issues", explore: "explore", performance: "performance", releases: "releases" }[page];
    return { area: "hub", section: section ?? "summary", orgId: null, sections: hubSections };
  }
  if (lower.startsWith("/account")) {
    const section = lower.startsWith("/account/preferences")
      ? "preferences"
      : lower.startsWith("/account/security")
        ? "security"
        : "profile";
    return { area: "account", section, orgId: null, sections: accountSections };
  }
  return { area: null, section: null, orgId: null, sections: [] };
}

/** Parámetros de una ficha (un registro de la empresa): al cambiar de empresa no tienen sentido y se quitan. */
const recordParams = [
  "userId",
  "employeeId",
  "baseId",
  "sourceId",
  "caseId",
  "approvalId",
  "contactId",
  "sessionId",
  "channelId",
  "email",
];

/**
 * C49 · al cambiar de empresa se queda en la misma sección y pestaña: la misma dirección con el id nuevo. Desde una ficha
 * (un registro de la otra empresa) va a la primera pantalla de su sección, con su pestaña de lista si la tenía.
 */
export function switchOrgHref(pathname: string, search: string, nextId: number): string {
  const place = placeOf(pathname, search);
  const params = new URLSearchParams(search);
  const inRecord = recordParams.some((key) => params.has(key)) || /\/detail\/\d+$/i.test(pathname);
  if (inRecord) {
    const section = orgSections.find((item) => item.key === place.section) ?? orgSections[0]!;
    return section.href(nextId);
  }
  params.set("id", String(nextId));
  params.delete("page");
  return `${pathname}?${params.toString()}`;
}

/**
 * Rutas del Admin nuevo (React), todas las de la especificación §4 desde R7: ninguna cae en el Platform Admin actual
 * (G5). Una dirección que no está aquí es «No encontramos esta página» (C40).
 */
const reactRoutes: RegExp[] = [
  /^\/$/,
  /^\/not-found$/i,
  /^\/error$/i,
  // R3 · Empresas y Personas, con las rutas Blazor que se funden.
  /^\/organizations(\/index|\/crear|\/detail(\/\d+)?|\/agregarusuario|\/agregarusuariocompletado|\/modulos|\/abrircomousuario|\/usuarios|\/invitaciones|\/propietario|\/recuperacionpropietario|\/usuario)?$/i,
  // R4 · Seguridad y acceso · Uso y soporte (EventosTecnicos → Diagnóstico, Trazas → Uso de IA).
  /^\/organizations\/(politicas|sesiones|recuperacionacceso|soporte|eventostecnicos|uso|trazas|auditoria)$/i,
  // R5 · Empleados IA y conocimiento (Capacidades y EmpleadoConocimiento → pestañas de la ficha, ConocimientoBase → base).
  /^\/organizations\/(empleados|empleado|capacidades|empleadoconocimiento|conocimiento|conocimientobase|conocimientodetalle|conocimientofuente|conocimientofuentenueva|conocimientoterminos|aprobaciones|aprobaciones\/politicas|aprobacion)$/i,
  // R6 · Mensajería (Client → la conversación simulada de ese cliente).
  /^\/organizations\/(bandejas|canales|reglas|conversaciones|conversacion|simulator|chat|client)$/i,
  /^\/ai(\/.*)?$/i,
  // R7 · Error Hub (cinco secciones) y Mi cuenta (Perfil, Preferencias y Seguridad con sus pestañas).
  /^\/errorhub(\/(issues|explore|performance|releases))?$/i,
  /^\/account(\/(profile|preferences|security(\/(password|mfa|sessions|activity))?))?$/i,
];

export const isReactRoute = (pathname: string) => reactRoutes.some((pattern) => pattern.test(pathname));
